import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [dark, setDark] = useState(() => localStorage.getItem("am-theme") !== "light");
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("am-cart")) || [];
    } catch {
      return [];
    }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [siteMode, setSiteMode] = useState(null);

  const loadSiteMode = async () => {
    try {
      const { data } = await axios.get(`${API}/site-mode`);
      setSiteMode(data.mode || "live");
    } catch {
      setSiteMode("live");
    }
  };

  useEffect(() => {
    loadSiteMode();
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("am-theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    localStorage.setItem("am-cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product) => {
    setCart((prev) => {
      const found = prev.find((p) => p.id === product.id);
      if (found) return prev.map((p) => (p.id === product.id ? { ...p, qty: Math.min(p.qty + 1, 20) } : p));
      return [...prev, { ...product, qty: 1 }];
    });
    setCartOpen(true);
  };

  const updateQty = (id, delta) =>
    setCart((prev) =>
      prev
        .map((p) => (p.id === id ? { ...p, qty: p.qty + delta } : p))
        .filter((p) => p.qty > 0)
    );

  const removeFromCart = (id) => setCart((prev) => prev.filter((p) => p.id !== id));
  const clearCart = () => setCart([]);

  const cartCount = useMemo(() => cart.reduce((s, p) => s + p.qty, 0), [cart]);
  const cartTotal = useMemo(() => cart.reduce((s, p) => s + p.qty * p.price, 0), [cart]);

  const checkAdmin = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setAdmin(data);
    } catch {
      setAdmin(false);
    }
  };

  return (
    <AppContext.Provider
      value={{ dark, setDark, cart, addToCart, updateQty, removeFromCart, clearCart, cartOpen, setCartOpen, cartCount, cartTotal, admin, setAdmin, checkAdmin, siteMode, loadSiteMode }}
    >
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
